import { useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { nearestGuineaCity } from "../lib/cityFromCoords";

/**
 * "📍 Près de moi" button.
 *
 * Requests GPS, maps to nearest Lokaso city, and calls `onLocated({ city, name })`.
 * Falls back to a toast if the user denies permission or is far from any city.
 */
export default function GeoLocateButton({ onLocated, variant = "pill", className = "" }) {
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    if (!navigator.geolocation) {
      toast.error("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoading(false);
        const { latitude, longitude } = pos.coords;
        const city = nearestGuineaCity(latitude, longitude);
        if (!city) {
          toast.message("Vous semblez être hors de Guinée — recherche élargie", {
            description: "Affichage des entreprises de toutes les villes.",
          });
          onLocated?.({ city: "", name: "" });
          return;
        }
        toast.success(`Recherche autour de ${city.name}`);
        onLocated?.({ city: city.slug, name: city.name });
      },
      (err) => {
        setLoading(false);
        if (err.code === 1) {
          toast.error("Activez la localisation pour trouver les entreprises proches");
        } else {
          toast.error("Localisation indisponible, réessayez plus tard");
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  };

  if (variant === "hero") {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid="geo-locate-hero-btn"
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-sm text-white transition-colors disabled:opacity-50 ${className}`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
        Près de moi
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid="geo-locate-btn"
      className={`inline-flex items-center gap-1.5 h-11 px-3 rounded-xl bg-lokaso-gold hover:brightness-95 text-lokaso-dark text-sm font-semibold whitespace-nowrap disabled:opacity-60 ${className}`}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
      <span className="hidden sm:inline">Près de moi</span>
    </button>
  );
}
